import type { Filter_1_0_0, Sort_1_0_0 } from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'
import type { RecentSearchHotelCarouselDecision } from 'home/recentSearchHotelCarousel/recentSearchHotelCarouselModel'

export const RECENT_SEARCH_HOTEL_CAROUSEL_MODULE_ID = 'recent-search-hotel-carousel'

export interface RecentSearchHotelCarouselAnalyticsData {
  recentSearchHotelCarousel: {
    correlationKey: string
    destinationIds: string
    checkIn?: string
    checkOut?: string
    searchedAt: string
    reason: string
    dateMode: string
    occupancy?: string
    variant?: string
    firstEligibleAt?: string
  }
}

export function isRecentSearchHotelCarouselAnalyticsData(
  data: unknown,
): data is RecentSearchHotelCarouselAnalyticsData {
  return !!data
    && typeof data === 'object'
    && 'recentSearchHotelCarousel' in data
}

function summariseOccupancy(rooms: Array<App.Occupants>): string {
  return rooms.map(room => {
    const childAges = room.childrenAge?.length ? `:${room.childrenAge.join('.')}` : ''
    return `${room.adults}a${room.children ?? 0}c${childAges}`
  }).join('|')
}

export function buildRecentSearchHotelCarouselAnalyticsData(
  decision: RecentSearchHotelCarouselDecision,
  extras?: { variant?: string, firstEligibleAt?: string },
): RecentSearchHotelCarouselAnalyticsData | undefined {
  if (!decision.context) return undefined
  return {
    recentSearchHotelCarousel: {
      correlationKey: decision.context.correlationKey,
      destinationIds: decision.context.placeIds.join(','),
      checkIn: decision.context.checkIn,
      checkOut: decision.context.checkOut,
      searchedAt: decision.context.searchedAt,
      reason: decision.reason,
      dateMode: decision.context.dateMode,
      occupancy: summariseOccupancy(decision.context.rooms),
      variant: extras?.variant,
      firstEligibleAt: extras?.firstEligibleAt,
    },
  }
}

export function buildRecentSearchHotelCarouselFilterContexts(
  data: RecentSearchHotelCarouselAnalyticsData,
): Array<Filter_1_0_0> {
  const context = data.recentSearchHotelCarousel
  const filters: Array<Filter_1_0_0> = [
    {
      type: 'Module',
      label: 'Module',
      value: RECENT_SEARCH_HOTEL_CAROUSEL_MODULE_ID,
    },
    {
      type: 'Destination',
      label: 'Searched destination',
      value: context.destinationIds,
    },
    {
      type: 'SearchCorrelation',
      label: 'Recent search correlation',
      value: context.correlationKey,
    },
    {
      type: 'Eligibility',
      label: 'Eligibility reason',
      value: context.reason,
    },
  ]

  if (context.checkIn && context.checkOut) {
    filters.push({
      type: 'StayDates',
      label: 'Searched stay',
      value: `${context.checkIn}/${context.checkOut}`,
    })
  }

  if (context.occupancy) {
    filters.push({
      type: 'Occupancy',
      label: 'Searched occupancy',
      value: context.occupancy,
    })
  }

  if (context.variant) {
    filters.push({
      type: 'ExperimentVariant',
      label: 'Experiment variant',
      value: context.variant,
    })
  }

  if (context.firstEligibleAt) {
    filters.push({
      type: 'FirstEligibleAt',
      label: 'First eligible time',
      value: context.firstEligibleAt,
    })
  }

  return filters
}

export function buildRecentSearchHotelCarouselSortContext(): Sort_1_0_0 {
  return { value: 'recommended' }
}
